import { syncedStore, getYjsDoc, getYjsValue } from '@syncedstore/core'
import { Node, Edge, OnNodesChange, OnEdgesChange } from '@xyflow/react'
import { useState, useEffect } from 'react'
import { WebsocketProvider } from 'y-websocket'
import * as Y from 'yjs'

import {
  initializeCursorStateSynced,
  Cursor,
} from '@/hooks/useCursorStateSynced'
import { initializeEdgesStateSynced } from '@/hooks/useEdgesStateSynced'
import { initializeNodesStateSynced } from '@/hooks/useNodesStateSynced'
import type { Project } from '@/stores/projectsStore'

// Define the structure of the synced store
interface ProjectStoreType {
  project: Project
  sharedMap: Record<string, any>
  arrayData: []
}

// Global variables
export let ydoc: Y.Doc
let websocketProvider: WebsocketProvider | null = null
let isInitialized = false

// Create the synced store
export const syncedProjectStore = syncedStore<ProjectStoreType>({
  project: {} as Project,
  sharedMap: {},
  arrayData: [],
})

// Hooks for synced state
let useCursorStateSynced: ReturnType<
  typeof initializeCursorStateSynced
> | null = null
let useNodesStateSynced: ReturnType<typeof initializeNodesStateSynced> | null =
  null
let useEdgesStateSynced: ReturnType<typeof initializeEdgesStateSynced> | null =
  null

//console.log('PASKAVITTU ' + import.meta.env.VITE_API_BASE_URL)

const baseUrl =
  import.meta.env.VITE_API_BASE_URL === 'http://localhost:4040'
    ? 'ws://localhost:4040/collaboration/connection'
    : 'wss://studio-redux.spechops.com/collaboration/connection'
console.log('baseUrl for syncedProjectStore is ' + baseUrl)

// Initialize the synced store
export const initializeSyncedStore = (docName: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    console.log('Initializing synced store...', docName)
    if (isInitialized) {
      console.log('Synced store already initialized.')
      destroyProvider()
    }

    ydoc = getYjsDoc(syncedProjectStore)

    // Create WebSocket provider
    websocketProvider = new WebsocketProvider(baseUrl, docName, ydoc, {
      params: { document: docName },
      protocols: [],
    })

    // Set up event listeners for the WebSocket provider
    websocketProvider.on('status', ({ status }: { status: string }) => {
      console.log('WebSocket connection status:', status)
      if (status === 'connected') {
        console.log('WebSocket connected. Resolving promise...')
        isInitialized = true
        resolve()
      }
    })

    websocketProvider.on('connection-error', (error: Error) => {
      console.error('WebSocket connection error:', error)
      reject(error)
    })

    websocketProvider.on('sync', () => {
      console.log(
        `[Browser] Sync event. State: ${JSON.stringify(ydoc.toJSON())}`
      )
      console.log('WebSocket synced.')
    })

    websocketProvider.on('message', (message: any) => {
      console.log('WebSocket message:', message)
    })

    websocketProvider.on('disconnect', () => {
      console.log('WebSocket disconnected. Resetting provider...')
      isInitialized = false
      destroyProvider()
      websocketProvider = null
    })

    websocketProvider.awareness.on('update', () => {
      //console.log('Awareness update', websocketProvider.awareness.getStates())
    })

    websocketProvider.on('connection-error', (error: Error) => {
      console.error('WebSocket connection error:', error)
      reject(error)
    })

    // Set up Yjs document update listener
    ydoc.on('update', (update: Uint8Array, origin: any) => {
      //console.log('Document updated', update, origin)
      Y.applyUpdate(ydoc, update)
    })

    // Initialize hooks
    useCursorStateSynced = initializeCursorStateSynced(ydoc)
    useNodesStateSynced = initializeNodesStateSynced(ydoc)
    useEdgesStateSynced = initializeEdgesStateSynced(ydoc)

    // Connect the provider
    websocketProvider.connect()

    isInitialized = true
    resolve()
  })
}

// Disconnect the WebSocket provider
export const disconnect = () => {
  if (websocketProvider) {
    websocketProvider.disconnect()
  }
}

// Connect the WebSocket provider
export const connect = () => {
  if (websocketProvider) {
    websocketProvider.connect()
  }
}

// Destroy the WebSocket provider
export const destroyProvider = () => {
  if (websocketProvider) {
    websocketProvider.destroy()
    websocketProvider = null
  }
  isInitialized = false
}

// Export the synced state
export const syncedState = getYjsValue(syncedProjectStore)

// Custom hook to ensure store is initialized
export function useSyncedProjectStore(docName: string) {
  const [isReady, setIsReady] = useState(isInitialized)

  useEffect(() => {
    if (!isInitialized) {
      initializeSyncedStore(docName).then(() => setIsReady(true))
    }
  }, [docName])

  return {
    isReady,
    useCursorStateSynced: useCursorStateSynced!,
    useNodesStateSynced: useNodesStateSynced!,
    useEdgesStateSynced: useEdgesStateSynced!,
  }
}

// For nodes
export function useInitializedSyncedProjectStore() {
  return {
    useNodesStateSynced: useNodesStateSynced!,
    useEdgesStateSynced: useEdgesStateSynced!,
  }
}
